import * as React from 'react';
import { useLocation, Outlet, matchPath, useNavigate } from 'react-router-dom';
import TopNav from './TopNav';
import EmailCategory from '@/components/EmailCategory';
import { useFolderStore } from '@/store/folderStore';
import { useFirebaseNotification } from '@/hooks/useFirebaseNotification';
import { useSocketNotification } from '@/hooks/useSocketNotification';
import { authService } from '@/services/auth';
import { useThemeStore } from '@/store/themeStore';
import './TopNav.css';
import { Sidebar } from './index';
import { SidebarProvider, useSidebar } from '@/contexts/SidebarContext';

function RootLayoutContent() {
  const { fetchFolders } = useFolderStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTheme, previewTheme } = useThemeStore();
  const { sidebarOpen } = useSidebar();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [notificationSystem, setNotificationSystem] = React.useState<'firebase' | 'socket' | 'both'>('both');
  const { isInitialized: isFirebaseInitialized } = useFirebaseNotification();
  const socket = useSocketNotification();

  React.useEffect(() => {
    const checkNotificationSystem = async () => {
      try {
        const isFirebaseAvailable = isFirebaseInitialized && 'Notification' in window;
        const isSocketAvailable = socket?.connected;

        if (isFirebaseAvailable && isSocketAvailable) {
          setNotificationSystem('both');
        } else if (isFirebaseAvailable) {
          setNotificationSystem('firebase');
        } else if (isSocketAvailable) {
          setNotificationSystem('socket');
        }
      } catch (error) {
        console.error('Error checking notification system:', error);
      }
    };

    checkNotificationSystem();
  }, [isFirebaseInitialized, socket?.connected, notificationSystem]);

  React.useEffect(() => {
    const isAddingAccount = location.state?.addAccount;
    if (!authService.isAuthenticated() && !isAddingAccount) {
      navigate('/login', { replace: true });
    }
  }, [navigate, location.pathname, location.state?.addAccount]);

  React.useEffect(() => {
    fetchFolders();
  }, [fetchFolders, isFirebaseInitialized]);

  const isEmailView = matchPath('/email/:folderPath/:id/:uid', location.pathname);
  const isProfileView = location.pathname === '/profile';
  const activeTheme = previewTheme || currentTheme;

  React.useEffect(() => {
    const root = document.documentElement;
    if (activeTheme) {
      root.classList.add('app-theme-active');
      root.style.setProperty('--app-theme-image', `url("${activeTheme}")`);
    } else {
      root.classList.remove('app-theme-active');
      root.style.removeProperty('--app-theme-image');
    }
  }, [activeTheme]);

  return (
    <div className="app-shell">
      <div className="app-shell__backdrop" aria-hidden="true" />

      <div className="app-shell__content container">
        <Sidebar />
        <div
          className={`main-content app-shell__main${isEmailView || isProfileView ? ' main-content--full-height' : ''}`}
          style={{
            marginLeft: isMobile ? 0 : sidebarOpen ? '' : '',
            transition: 'margin-left 0.3s ease',
          }}
        >
          <TopNav />
          {!isEmailView && !isProfileView && <EmailCategory />}
          <div
            className={`content-area${isEmailView ? ' content-area--email-view' : ''}${isProfileView ? ' content-area--profile' : ''}`}
          >
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}

export function RootLayout() {
  return (
    <SidebarProvider>
      <RootLayoutContent />
    </SidebarProvider>
  );
}
